import SendIcon from '@mui/icons-material/Send';
import { Avatar, IconButton, InputBase, Stack, useTheme } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ThreeDots } from 'react-loader-spinner';
import { makeStyles } from 'tss-react/mui';

interface Props {
  chatMessageFieldInput: string;
  senderLinkedinAccount: { _id: string; userEmail: string; firstName?: string; lastName?: string } | undefined;
  sendMessageLoading: boolean;
  autoSyncLoading: boolean;

  handleChatMessageChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleSendMessage(): void;
}

const ChatMessageField: React.FC<Props> = ({
  chatMessageFieldInput,
  senderLinkedinAccount,
  sendMessageLoading,
  autoSyncLoading,
  handleChatMessageChange,
  handleSendMessage,
}) => {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();

  const getInitials = () => {
    const firstInitial = senderLinkedinAccount?.firstName ? senderLinkedinAccount.firstName.charAt(0) : '';
    const lastInitial = senderLinkedinAccount?.lastName ? senderLinkedinAccount.lastName.charAt(0) : '';
    return `${firstInitial}${lastInitial}`;
  };

  const getFullName = () => {
    const firstName = senderLinkedinAccount?.firstName ? senderLinkedinAccount.firstName : '';
    const lastName = senderLinkedinAccount?.lastName ? senderLinkedinAccount.lastName : '';
    return `${firstName} ${lastName}`;
  };

  return (
    <Stack className={classes.chatMessageField} direction={'row'} alignItems={'flex-end'} gap={1.5}>
      <Avatar className={classes.linkedinAccountAvatar}>{getInitials()}</Avatar>
      <InputBase
        className={classes.textField}
        multiline={true}
        minRows={1}
        maxRows={6}
        value={chatMessageFieldInput}
        placeholder={t('sendMessagePlaceholder', { name: getFullName() })}
        disabled={sendMessageLoading}
        inputProps={{ autoComplete: 'off' }}
        onChange={handleChatMessageChange}
      />
      <IconButton
        className={cx(classes.sendButton)}
        disabled={sendMessageLoading || autoSyncLoading || chatMessageFieldInput.trim() === ''}
        onClick={handleSendMessage}
      >
        {!sendMessageLoading ? <SendIcon /> : <ThreeDots color={theme.palette.greys.medium} height={18} width={18} radius={3} />}
      </IconButton>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  chatMessageField: {
    padding: '16px 24px',
    borderTop: `1px solid ${theme.palette.greys.lighter}`,
    background: '#FFF',
  },
  linkedinAccountAvatar: {
    width: '32px',
    height: '32px',
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 700,
    background: '#B0A9FF',
    color: '#FFF',
  },
  textField: {
    flex: 1,
    minHeight: '48px',
    padding: '12px 14px',
    border: `1px solid ${theme.palette.greys.lighter}`,
    borderRadius: '12px',
    fontSize: theme.typography.pxToRem(15),
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
    },
    '& textarea': {
      padding: 0,
    },
  },
  sendButton: {
    width: '40px',
    height: '40px',
    flexShrink: 0,
    borderRadius: '8px',
    background: 'linear-gradient(to bottom, #5044FF, #4AA4FF)',
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(18),
      color: '#FFF',
    },
    '&:hover': {
      background: 'linear-gradient(to bottom, #4AA4FF, #5044FF)',
    },
    '&.Mui-disabled': {
      background: theme.palette.greys.lightest,
    },
  },
}));

export default ChatMessageField;
